import { useQuery, useQueryClient } from '@tanstack/react-query'
import { cachePatch, useOptimisticMutation } from '@/lib/optimistic-mutation'
import api from '@/lib/api'
import { schoolSettingsKeys } from '@/lib/queryKeys'

import type { School } from '@/types/api'
import { REFERENCE_DATA_STALE_TIME } from '@/lib/staleTimes'

/**
 * Re-exported from `lib/queryKeys`, which owns the whole key space — see the note
 * there. A query still names its key from the module it belongs to.
 */
export { schoolSettingsKeys }

export function useSchoolSettings() {
  return useQuery({
    queryKey: schoolSettingsKeys.current(),
    queryFn: async (): Promise<School> => {
      const res = await api.get<{ school: School }>('/api/schools/current')
      return res.data.school
    },
    // Every page mounts this: the sidebar asks it whether the school runs the
    // gradebook at all (`useGradebookEnabled`), and half the forms ask it which
    // identifiers the school issues (`useIdentifierPolicy`). At the app-wide
    // minute that is a request on the critical path of every navigation an admin
    // makes all day, for an answer that changes when somebody visits Settings.
    //
    // Safe because a write still reaches it immediately: `invalidateQueries`
    // marks an entry stale whatever its staleTime says, and `useUpdateSchoolSettings`
    // writes the server's response straight into this key. Turning the gradebook
    // off does not wait five minutes to take effect.
    staleTime: REFERENCE_DATA_STALE_TIME,
  })
}

/**
 * Which of the school's own identifiers the app has at all.
 *
 * Read from the school settings rather than passed down, so a form and the API
 * route it posts to cannot disagree about whether a student number exists - the
 * alternative is a prop that some call site forgets, and a field that asks for
 * something the school does not issue.
 *
 * False is not "optional". It shipped that way, and an optional box for a number
 * the school does not have is a question nobody on the front desk can answer. It
 * now means the field is gone: no form asks for it, no table or detail row shows
 * it, and the API writes nothing to it. Records that carry a value from before
 * the setting was turned off keep it - it simply stops being rendered, which is
 * what `useIdentifierText()` is for. Show a column or an input only behind the
 * matching flag here.
 *
 * Defaults to having all three while the answer is not in hand - the query still
 * loading, or a response that predates the setting. That is a statement about
 * missing data, not about new schools: a school created today is written with all
 * three off (`db/defaults/newSchool.ts`), and the API always sends a real value,
 * so this fallback only ever covers the gap before it arrives. See
 * `services/identifierPolicy.ts` for the server's half.
 */
export interface IdentifierPolicy {
  studentId: boolean
  employeeId: boolean
  courseCode: boolean
}

export function useIdentifierPolicy(): IdentifierPolicy {
  const { data: school } = useSchoolSettings()
  return {
    studentId: school?.studentIdsEnabled ?? true,
    employeeId: school?.employeeIdsEnabled ?? true,
    courseCode: school?.courseCodesEnabled ?? true,
  }
}

/**
 * Whether the school runs the gradebook at all.
 *
 * The one reader of `school.gradebookEnabled` in the app, so the default lives
 * in one place: on. Not because an unconfigured school runs the gradebook - a
 * school created today is written with it off (`db/defaults/newSchool.ts`) - but
 * because the only way to reach this fallback is a settings request that has not
 * answered yet or failed, and a network hiccup must not silently take a working
 * gradebook away from every school that uses one.
 *
 * That default is also why anything that *renders* a grade waits for the query
 * to settle rather than calling this: see `components/auth/GradebookRoute.tsx`.
 * A link drawn a moment early is a link; a page rendered a moment early is a
 * grade.
 */
export function useGradebookEnabled(): boolean {
  const { data: school } = useSchoolSettings()
  return school?.gradebookEnabled ?? true
}

export interface UpdateSchoolSettingsInput {
  name?: string
  timezone?: string
  emailNotifications?: boolean
  gradeUpdateNotifications?: boolean
  assignmentNotifications?: boolean
  studentIdsEnabled?: boolean
  employeeIdsEnabled?: boolean
  courseCodesEnabled?: boolean
  /** Turns the grades half of the app on or off. Admin-only to write. */
  gradebookEnabled?: boolean
  /**
   * Admin-only settings. They go through the same PUT as everything above, and
   * are simply absent from a non-admin's GET - which is why the `School` type does
   * not carry them and the Settings page reads them through its own intersection.
   */
  normalizeEmptyCategories?: boolean
  gpaScaleMax?: number
  /** Null clears the bound, meaning every posted grade counts toward the GPA. */
  gpaMinGradeLevel?: number | null
  gpaMaxGradeLevel?: number | null
  gpaCreditWeighted?: boolean
  /**
   * Consent to send an enrolment document to Anthropic to be read into the intake
   * form. Off until an admin turns it on, and inert without an `ANTHROPIC_API_KEY`
   * on the deployment - both halves are required.
   */
  aiExtractionEnabled?: boolean
}

/**
 * The one write in the app that already wrote through rather than invalidating —
 * the server's response *is* the new cache value, so there is no refetch to wait
 * for and nothing to go stale. The optimistic patch here only closes the
 * remaining gap: a notification toggle now moves under the cursor rather than
 * after the round trip, and rolls back if the save is refused.
 */
export function useUpdateSchoolSettings() {
  const queryClient = useQueryClient()
  return useOptimisticMutation({
    mutationFn: async (data: UpdateSchoolSettingsInput): Promise<School> => {
      const res = await api.put<{ school: School }>('/api/schools/current', data)
      return res.data.school
    },
    optimistic: (data) => [
      cachePatch<School>(
        schoolSettingsKeys.current(),
        (school) => ({ ...school, ...data }),
        { exact: true }
      ),
    ],
    onSuccess: (school) => {
      queryClient.setQueryData(schoolSettingsKeys.current(), school)
    },
  })
}
